// 管理员顶栏工单铃铛
// - 每 30 秒拉一次 /api/tickets(全量),统计未读数(用户最后回复时间 > 上次查看时间)
// - 点击跳到 /admin/tickets 页面
// - 单独打开某张工单详情时,通过 onView 回调标记已读(在 AdminTickets 里调用)
// 复用 /api/tickets:返回 updated_at + last_reply_by
import React, { useEffect, useState, useCallback } from 'react';
import { IconCustomerSupport } from '@douyinfe/semi-icons';
import { API } from '../helpers';
import { useHashRoute, navigate } from '../router';

const READ_KEY = 'mt_admin_tickets_last_read';

// —— 共享的「标记已读」函数:AdminTickets 详情打开时调用 ——
const _listeners = new Set();
const getReadAt = () => {
  try { return parseInt(sessionStorage.getItem(READ_KEY) || '0', 10) || 0; } catch { return 0; }
};
const persist = (n) => {
  try { sessionStorage.setItem(READ_KEY, String(n)); } catch (_) {}
  _listeners.forEach((fn) => fn(n));
};
export const markAdminTicketsRead = () => persist(Date.now());
// 标记当前打开的那条工单已读(其他不标记),用 updated_at 作 lastRead
export const markTicketReadByUpdatedAt = (updatedAt) => {
  if (!updatedAt) return;
  const cur = getReadAt();
  if (Number(updatedAt) > cur) persist(Number(updatedAt));
};
const subscribe = (fn) => { _listeners.add(fn); return () => _listeners.delete(fn); };

const AdminTicketsBell = () => {
  const [reloadKey, setReloadKey] = useState(0);
  const [tickets, setTickets] = useState([]);
  const [lastReadAt, setLastReadAt] = useState(getReadAt);
  const path = useHashRoute();

  // 订阅已读时间戳变化
  useEffect(() => {
    const unsub = subscribe((n) => setLastReadAt(n));
    return unsub;
  }, []);

  // 监听 sessionStorage 外部变化(其他标签页/手动清理):用 storage 事件
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === READ_KEY) setLastReadAt(getReadAt());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // 30 秒轮询 + 路径切到 tickets 也立即拉一次
  useEffect(() => {
    let alive = true;
    const load = () => {
      API.get('/api/tickets')
        .then((r) => { if (alive && r.data?.success) setTickets(r.data.data); })
        .catch(() => {});
    };
    load();
    const id = setInterval(load, 30000);
    return () => { alive = false; clearInterval(id); };
  }, [reloadKey]);

  useEffect(() => {
    if (path.startsWith('/admin/tickets')) setReloadKey((k) => k + 1);
  }, [path]);

  // 未读数:用户最后回复 且 updated_at > 上次查看时间
  const unreadCount = tickets.filter(
    (t) => t.status === 1 && t.last_reply_by === 1 && Number(t.updated_at || 0) > lastReadAt
  ).length;

  const onClick = useCallback(() => {
    navigate('/admin/tickets');
  }, []);

  // 自己画红点:Semi Badge 对 Button children 的 sup 定位有问题,
// 直接用相对定位的 span 包裹,避免样式冲突
const btnStyle = {
  position: 'relative',
  display: 'inline-flex',
  alignItems: 'center',
  padding: '6px 10px',           // 按钮 padding 增加,给图标留更多呼吸空间
  border: 'none',
  background: 'transparent',
  color: '#52525b',
  cursor: 'pointer',
  borderRadius: 4,
};
const dotStyle = {
  position: 'absolute',
  top: 0,                       // 紧贴按钮上边(悬浮于外侧)
  right: 0,                     // 紧贴按钮右边
  transform: 'translate(40%, -40%)',  // 整体向外偏移 40%,形成明显间距
  minWidth: 20,
  height: 20,
  padding: '0 6px',
  borderRadius: 10,
  background: '#f56565',
  color: '#fff',
  fontSize: 12,
  fontWeight: 600,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  lineHeight: 1,
  boxShadow: '0 0 0 2px #fff', // 白色描边让红点与按钮分隔
};

const IconStyle = { fontSize: 20 };  // 图标本身略放大

return (
  <button
    type="button"
    aria-label="工单"
    onClick={onClick}
    style={btnStyle}
  >
    <IconCustomerSupport style={IconStyle} />
    {unreadCount > 0 && (
      <span style={dotStyle} data-testid="bell-unread">
        {unreadCount > 99 ? '99+' : unreadCount}
      </span>
    )}
  </button>
  );
};

export default AdminTicketsBell;